import { DropdownElements } from './_dom-manager'
import { DropdownState, DropdownOptions } from './types'

/**
 * Класс для управления обработчиками событий dropdown
 */
export class DropdownEventHandlers {
  private element: HTMLElement
  private elements: DropdownElements
  private state: DropdownState
  private options: DropdownOptions

  // Callback функции
  private onToggle: () => void
  private onClose: () => void
  private onUpdateValue: (_value: string | number) => void
  private onFilterOptions: () => void
  private onReset: () => void
  private onSetValue: (_value: string | number | Array<string | number>) => void

  /**
   * Создаёт экземпляр DropdownEventHandlers
   *
   * @param {HTMLElement} element - Корневой элемент
   * @param {DropdownElements} elements - DOM элементы
   * @param {DropdownState} state - Состояние
   * @param {DropdownOptions} options - Опции
   * @param {Object} callbacks - Объект с callback функциями
   */
  constructor(
    element: HTMLElement,
    elements: DropdownElements,
    state: DropdownState,
    options: DropdownOptions,
    callbacks: {
      onToggle: () => void
      onClose: () => void
      onUpdateValue: (_value: string | number) => void
      onFilterOptions: () => void
      onReset: () => void
      onSetValue: (_value: string | number | Array<string | number>) => void
    }
  ) {
    this.element = element
    this.elements = elements
    this.state = state
    this.options = options
    this.onToggle = callbacks.onToggle
    this.onClose = callbacks.onClose
    this.onUpdateValue = callbacks.onUpdateValue
    this.onFilterOptions = callbacks.onFilterOptions
    this.onReset = callbacks.onReset
    this.onSetValue = callbacks.onSetValue
  }

  /**
   * Обработчик клика по триггеру
   * @param {Event} event - Событие клика
   */
  private handleTriggerClick = (event: Event): void => {
    event.stopPropagation()
    this.onToggle()
  }

  /**
   * Обработчик клика вне дропдауна
   * @param {Event} event - Событие клика
   */
  private handleOutsideClick = (event: Event): void => {
    const target = event.target as HTMLElement
    if (
      this.state.opened &&
      (!target.closest('[data-dropdown]') ||
        target.closest('[data-close-dropdown]'))
    ) {
      this.onClose()
    }
  }

  /**
   * Обработчик нажатия клавиши Escape
   * @param {KeyboardEvent} event - Событие нажатия клавиши
   */
  private handleEscapeKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.state.opened) {
      this.onClose()
    }
  }

  /**
   * Обработчик ввода в поле поиска
   * @param {Event} event - Событие ввода
   */
  private handleSearchInput = (event: Event): void => {
    const target = event.target as HTMLInputElement
    this.state.searchValue = target.value.toLowerCase()
    this.onFilterOptions()
  }

  private handleDefaultClick = (): void => {
    if (this.options.multiple) {
      this.onReset()
    }
  }

  private handleVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') {
      this.onClose()
    }
  }

  /**
   * Обработчик клика по опции
   * @param {Event} event - Событие клика
   */
  private handleOptionClick = (event: Event): void => {
    const target = event.target as HTMLElement
    const button = target.closest('[data-dropdown-option]') as HTMLButtonElement

    if (button) {
      event.stopPropagation()
      const value = button.dataset.dropdownValue
      // eslint-disable-next-line no-undefined
      if (value !== undefined) {
        this.onUpdateValue(value)
      }
    }
  }

  /**
   * Обработчик пользовательского события "set-value"
   * @param {Event} event - Событие
   */
  private handleSetValue = (event: Event): void => {
    const customEvent = event as CustomEvent
    const { value } = customEvent.detail
    this.onSetValue(value)
  }

  private handleDropdownReset = (): void => {
    this.onReset()
  }

  public bind(): void {
    // Клик по триггеру
    this.elements.trigger?.addEventListener('click', this.handleTriggerClick)

    // Поиск
    if (this.elements.searchInput) {
      this.elements.searchInput.addEventListener(
        'input',
        this.handleSearchInput
      )
    }

    // Опция "по умолчанию"
    if (this.elements.defaultOptionButton) {
      this.elements.defaultOptionButton.addEventListener(
        'click',
        this.handleDefaultClick
      )
    }

    // Клики по опциям (делегирование событий)
    if (this.elements.optionsContainer) {
      this.elements.optionsContainer.addEventListener(
        'click',
        this.handleOptionClick
      )
    }

    // Закрытие при потере видимости страницы
    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    // Внешние события
    this.element.addEventListener('set-value', this.handleSetValue)
    this.element.addEventListener('dropdown-reset', this.handleDropdownReset)
  }

  public addDocumentListeners(): void {
    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleEscapeKey)
  }

  public removeDocumentListeners(): void {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleEscapeKey)
  }

  public destroy(): void {
    this.elements.trigger?.removeEventListener('click', this.handleTriggerClick)

    if (this.elements.searchInput) {
      this.elements.searchInput.removeEventListener(
        'input',
        this.handleSearchInput
      )
    }

    if (this.elements.defaultOptionButton) {
      this.elements.defaultOptionButton.removeEventListener(
        'click',
        this.handleDefaultClick
      )
    }

    if (this.elements.optionsContainer) {
      this.elements.optionsContainer.removeEventListener(
        'click',
        this.handleOptionClick
      )
    }

    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    )
    this.element.removeEventListener('set-value', this.handleSetValue)
    this.element.removeEventListener('dropdown-reset', this.handleDropdownReset)

    this.removeDocumentListeners()
  }
}
